import React, { useEffect, useRef, useState } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5map from "@amcharts/amcharts5/map";
import am5geodata_worldLow from "@amcharts/amcharts5-geodata/worldHigh"; //need to install this separately
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useSelector } from "react-redux";
import { Box, CircularProgress, Paper } from "@mui/material";

export default function GeoChart() {
	const chartRef = useRef(null);
	const [isChartLoading, setIsChartLoading] = useState(true);
	const { geoData, allDataLoaded, drawerOpen } = useSelector(
		(state) => state.dashboard
	);

	const chartWidth = drawerOpen ? "75%" : "100%";

	useEffect(() => {
		setIsChartLoading(true);

		if (!allDataLoaded || !geoData?.length || !chartRef.current) {
			return;
		}

		// Create root and chart
		const root = am5.Root.new(chartRef.current);

		// Set themes
		root.setThemes([am5themes_Animated.new(root)]);

		// Create chart
		let chart = root.container.children.push(
			am5map.MapChart.new(root, {
				homeZoomLevel: 3.5,
				homeGeoPoint: { longitude: 10, latitude: 52 },
				wheelSensitivity: 5,
			})
		);

		// Create world polygon series
		let worldSeries = chart.series.push(
			am5map.MapPolygonSeries.new(root, {
				geoJSON: am5geodata_worldLow,
				exclude: ["AQ"],
			})
		);

		worldSeries.mapPolygons.template.setAll({
			fill: am5.color(0xaaaaaa),
		});

		worldSeries.events.on("datavalidated", () => {
			chart.goHome();
			setIsChartLoading(false);
		});

		// Add legend
		let legend = chart.children.push(
			am5.Legend.new(root, {
				useDefaultMarker: true,
				centerX: am5.p50,
				x: am5.p50,
				centerY: am5.p100,
				y: am5.p100,
				dy: -20,
				background: am5.RoundedRectangle.new(root, {
					fill: am5.color(0xffffff),
					fillOpacity: 0.2,
				}),
			})
		);

		legend.valueLabels.template.set("forceHidden", true);

		chart.chartContainer.get("background").events.on("click", function () {
			chart.goHome();
		});

		chart.set("zoomControl", am5map.ZoomControl.new(root, {}));

		const geoChartList = geoData.map((row) => {
			const { countryCode, ...rest } = row;
			return { id: countryCode, ...rest };
		});

		let purpleList = [geoChartList.filter((p) => p.scenarioRatio == 0)];
		const magentaList = [
			geoChartList.filter(
				(p) => p.scenarioRatio > 0 && p.scenarioRatio <= 50
			),
		];
		const maroonList = [
			geoChartList.filter(
				(p) => p.scenarioRatio > 50 && p.scenarioRatio <= 75
			),
		];
		const yellowList = [geoChartList.filter((p) => p.scenarioRatio > 75)];

		const mydata = [
			{
				name: "Nothing is blocked",
				data: purpleList[0],
				color: "green",
			},
			{
				name: "1 to 50%",
				data: magentaList[0],
				color: "orange",
			},
			{
				name: "51 to 76%",
				data: maroonList[0],
				color: "#ff8c00",
			},
			{
				name: "76 to 100%",
				data: yellowList[0],
				color: "red",
			},
		];

		am5.array.each(mydata, function (group) {
			let countries = [];
			let color = group.color;

			am5.array.each(group.data, function (country) {
				countries.push(country.id);
			});

			let polygonSeries = chart.series.push(
				am5map.MapPolygonSeries.new(root, {
					geoJSON: am5geodata_worldLow,
					include: countries,
					name: group.name,
					fill: color,
				})
			);

			polygonSeries.mapPolygons.template.setAll({
				tooltipText:
					"{name} - Blocked order : {blockedOrder}  ,Ratio : {orderRatio}",
				interactive: true,
				fill: color,
				strokeWidth: 2,
			});

			polygonSeries.mapPolygons.template.states.create("hover", {
				fill: am5.Color.brighten(color, -0.3),
			});

			polygonSeries.mapPolygons.template.events.on(
				"pointerover",
				function (ev) {
					ev.target.series.mapPolygons.each(function (polygon) {
						polygon.states.applyAnimate("hover");
					});
				}
			);

			polygonSeries.mapPolygons.template.events.on(
				"pointerout",
				function (ev) {
					ev.target.series.mapPolygons.each(function (polygon) {
						polygon.states.applyAnimate("default");
					});
				}
			);
			polygonSeries.data.setAll(group.data);

			legend.data.push(polygonSeries);
		});

		return () => {
			root.dispose();
		};
	}, [allDataLoaded, geoData]);

	return (
		<Paper
			elevation={3}
			sx={{
				position: "relative",
				width: chartWidth,
				height: "500px",
				marginTop: 3.75,
			}}
		>
			{isChartLoading && (
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						backgroundColor: "rgba(255, 255, 255, 0.7)",
						zIndex: 1,
					}}
				>
					<CircularProgress />
				</Box>
			)}
			<div
				ref={chartRef}
				style={{
					width: "100%",
					height: "100%",
				}}
			></div>
		</Paper>
	);
}
